/**
 * AtlasTownLifeModule — the TOWN window and the player's SERVICE POST (TOWN LIFE, 2026-09-24).
 *
 * The Town window (menu bar → Town) hosts the town-life tabs other modules register (the Races tab is the racing
 * module's) and its own:
 *   POSTS       the service posts the town you stand in keeps (the society wave's 27 — publican, healer, watch,
 *               scribe …): take one at its venue (you hold it beside the town's own versona holders, one player a
 *               post a town), and while you hold it: duty time, the wage owed and paid, customers served, and a
 *               way back to the venue. ON DUTY this module pings the server every 20 s while you stand at the
 *               venue (THE DUTY LAW: only presence at the venue counts), and when the server sends a CUSTOMER — a
 *               real resident with a need — it marks them over their head and E serves them in person.
 *   FIREWORKS   launch a rocket (a kind, a palette) or a whole choreographed show from where you stand.
 *   CAMP        pitch a camp (a fire ring with log seats, 30 minutes, anyone may warm by it) or put yours out.
 *   BOUNTIES / CONTRACTS  what you have running, and the nearest terminal / board.
 * Levers: `__townLife`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { interactFocus } from '../lib/interact_focus.js';
import { onInteract, interactPrompt, interactKey } from '../lib/interact_input.js';
import { groundYIn } from '../lib/grounding.js';
import { SERVICE_POSTS, servicePost, postTierOf } from '../lib/service_posts.js';
import { settlementSites } from '../../../shared/settlements.js';
import { townSlots } from '../../../shared/town_layout.js';
import {
  FIREWORK_KINDS, FIREWORK_LABELS, FIREWORK_PALETTES, FIREWORK_SOV, FIREWORK_SHOW_SOV, CAMP_LIFE_S, DUTY_PING_S, SERVE_REACH_M,
  untilText, compassOf, type FireworkKind,
} from '../lib/town_life.js';
import { townName, commonsSeedNow } from '../lib/town_names.js';
import { el, btn, input, select, tabs, head, chip, muted, field, card, act, debounced, ms } from './town/town_ui.js';
import { registerTownTab, townTabs } from './town/town_tabs.js';
import { softRender, fieldByKey } from '../lib/soft_render.js';
// INTERIOR VENUES (the interiors programme, slice E, 2026-10-01): a post whose venue is BOUND to a building is stood
// inside it — `venueOfPostRow` answers null for an unbound post and while `platform_flag interior_venues_v1` is off,
// and every line below then runs as it did before the slice.
import { insideVenue, mayServeAt, VENUE_ARRIVE_M } from '../lib/venue_binding.js';
import { venueOfPostRow, venueOfSlot, venueCounter, venueTitle, venuePlaceText } from '../lib/venue_client.js';
import { COUNTER_REACH_M } from '../lib/venue_anchor.js';

interface PostRow { identity: Identity; settlementId: bigint; postKey: string; venueX: number; venueZ: number; dutyTodayS: number; totalDutyS: number; wageDueS: number; served: number; earned: bigint; lastDutyAt: { microsSinceUnixEpoch: bigint } }
interface RequestRow { requestId: bigint; holder: Identity; versonaId: bigint; need: string; expiresAt: { microsSinceUnixEpoch: bigint } }
interface CampRow { campId: bigint; owner: Identity; worldId: bigint; x: number; z: number; name: string; expiresAt: { microsSinceUnixEpoch: bigint } }

const DUTY_RADIUS_M = 30;
const DUTY_ROUTE_RADIUS_M = 150;
const isRoutePost = (key: string): boolean => { const d = servicePost(key); return !!d && (d.venue === 'route' || d.venue === 'site'); };
const dutyRadius = (key: string): number => (isRoutePost(key) ? DUTY_ROUTE_RADIUS_M : DUTY_RADIUS_M);
const postLabel = (key: string): string => { const s = key.startsWith('svc:') ? key.slice(4) : key; return s.split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join(' '); };

export class AtlasTownLifeModule implements IModule {
  readonly name = 'AtlasTownLife';
  private app!: App;
  private scene!: THREE.Scene;
  private win: UiWindow | null = null;
  private tab = 'races';
  private tabHost: HTMLDivElement | null = null;
  /** The tab `tabHost` was made for (a new host per tab — see render). */
  private tabHostFor = '';
  private subbed = false;
  private pingAt = 0;
  private pingNote = '';
  private customerMark: THREE.Sprite | null = null;
  private prompt: HTMLElement | null = null;
  private serveId: bigint | null = null;
  private hud: HTMLDivElement | null = null;
  private fwKind: FireworkKind = 'peony';
  private fwPalette = 0;
  /** The Commons seed, read once (the venue lookups ask it every frame a post is held). */
  private seedMemo = '';
  private seedNow(): string { return this.seedMemo || (this.seedMemo = commonsSeedNow()); }
  /** F2: the bound Interact key AND the pad's (`atlas:place-requested`), one press — lib/interact_input.ts. */
  private offInteract: (() => void) | null = null;
  private readonly rerender = debounced(() => { if (this.win?.isOpen() && (this.tab === 'posts' || this.tab === 'fire' || this.tab === 'bounties' || this.tab === 'contracts')) this.renderTab(); }, 300);

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    registerTownTab({ key: 'posts', label: 'Service posts', order: 2, render: (h) => this.renderPosts(h) });
    registerTownTab({ key: 'fire', label: 'Fireworks & camp', order: 3, render: (h) => this.renderFire(h) });
    registerTownTab({ key: 'bounties', label: 'Bounties', order: 4, render: (h) => this.renderBounties(h) });
    registerTownTab({ key: 'contracts', label: 'Contracts', order: 5, render: (h) => this.renderContracts(h) });
    bus.on('atlas:open-town-life', (p) => this.open(p?.tab));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => { this.win?.setOpen(false); this.dropMark(); });
    const pr = document.createElement('div');
    pr.id = 'post-serve-prompt';
    pr.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-good) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(pr);
    this.prompt = pr;
    const h = document.createElement('div');
    h.id = 'post-hud';
    h.style.cssText = 'position:fixed;top:210px;right:16px;z-index:31;display:none;flex-direction:column;gap:3px;max-width:300px;pointer-events:none;'
      + 'padding:6px 10px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 84%, transparent);border:1px solid color-mix(in srgb, var(--ax-good) 45%, transparent);'
      + 'font:600 12.5px var(--ax-font);color:var(--ax-text)';
    document.body.appendChild(h);
    this.hud = h;
    this.offInteract = onInteract(() => this.key());
    app.world.addSystem(makeSystem('TownLifePost', Phase.UI, 39, () => this.tick()));
    (window as unknown as Record<string, unknown>).__townLife = {
      open: (tab?: string) => this.open(tab), close: () => this.win?.setOpen(false), state: () => this.state(),
      ping: () => this.ping(true),
    };
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM player_post', 'SELECT * FROM post_request', 'SELECT * FROM player_camp']); } catch { /* reconnect */ }
    const db = conn.db as unknown as Record<string, { onInsert?(cb: (c: unknown, r: RequestRow) => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    for (const n of ['playerPost', 'postRequest', 'playerCamp']) {
      const t = db[n];
      t?.onInsert?.((_c, r) => {
        this.rerender();
        if (n === 'postRequest' && conn.identity && r.holder?.isEqual?.(conn.identity)) bus.emit('atlas:toast', { text: `A customer: ${r.need}`, kind: 'info', ms: 5000 });
      });
      t?.onUpdate?.(() => this.rerender()); t?.onDelete?.(() => this.rerender());
    }
  }

  // ── Rows ────────────────────────────────────────────────────────────────────
  private myPost(): PostRow | null {
    const me = atlas.conn?.identity;
    const t = (atlas.conn?.db as unknown as { playerPost?: { identity: { find(i: Identity): PostRow | undefined } } } | undefined)?.playerPost;
    return me && t ? t.identity.find(me) ?? null : null;
  }
  private myRequests(): RequestRow[] {
    const me = atlas.conn?.identity;
    const t = (atlas.conn?.db as unknown as { postRequest?: { iter(): Iterable<RequestRow> } } | undefined)?.postRequest;
    if (!me || !t) return [];
    return [...t.iter()].filter((r) => r.holder.isEqual(me) && ms(r.expiresAt) > Date.now());
  }
  private holdersOf(sid: bigint, key: string): string[] {
    const t = (atlas.conn?.db as unknown as { playerPost?: { iter(): Iterable<PostRow> } } | undefined)?.playerPost;
    const out: string[] = [];
    for (const p of t?.iter() ?? []) if (p.settlementId === sid && p.postKey === key) {
      try { const a = atlas.conn?.db.account.identity.find(p.identity); out.push(a ? (a.displayName || a.username) : '?'); } catch { out.push('?'); }
    }
    return out;
  }
  private versonaPos(id: bigint): { x: number; z: number } | null {
    try {
      const s = (atlas.conn?.db as unknown as { versonaState?: { versonaId: { find(i: bigint): { curX: number; curZ: number } | undefined } } } | undefined)?.versonaState?.versonaId.find(id);
      return s ? { x: s.curX, z: s.curZ } : null;
    } catch { return null; }
  }

  /** The town I stand in: the nearest site within 4 km, a district's capital (the server's settlementAt). */
  private townHere(): { sid: bigint; site: ReturnType<typeof settlementSites>[number] } | null {
    const seed = commonsSeedNow();
    if (!seed || atlas.currentWorldId !== atlas.commonsWorldId) return null;
    const sites = settlementSites(seed);
    let best: (typeof sites)[number] | null = null, bd = 4000 * 4000;
    for (const s of sites) { const d = (s.centerX - atlas.localX) ** 2 + (s.centerZ - atlas.localZ) ** 2; if (d < bd) { bd = d; best = s; } }
    if (!best) return null;
    const cap = (best as unknown as { capitalId?: number }).capitalId;
    if (best.tier === 'district' && cap !== undefined) { const c = sites.find((s) => s.id === cap); if (c) best = c; }
    return { sid: BigInt(best.id + 1), site: best };
  }
  private venueOf(site: ReturnType<typeof settlementSites>[number], key: string): { x: number; z: number } {
    const def = servicePost(key);
    const venueKey = isRoutePost(key) ? 'plaza' : def?.venue ?? 'plaza';
    let vx = site.centerX, vz = site.centerZ, best = Infinity;
    for (const s of townSlots(commonsSeedNow(), site)) {
      const k = s.key.includes(':') ? s.key.slice(s.key.indexOf(':') + 1) : s.key;
      if (k !== venueKey) continue;
      const d = Math.hypot(s.x - atlas.localX, s.z - atlas.localZ);
      if (d < best) { best = d; vx = s.x; vz = s.z; }
    }
    return { x: vx, z: vz };
  }

  // ── The frame: duty pings, the customer mark, E to serve ───────────────────
  private tick(): void {
    const post = this.myPost();
    const now = performance.now();
    if (!post || atlas.currentWorldId !== atlas.commonsWorldId) { this.dropMark(); this.showServe(null); this.hudOff(); return; }
    // INTERIOR VENUES: `venue` = the building this post is kept in (null = its slot, the pre-slice rule).
    const venue = venueOfPostRow(this.seedNow(), post);
    const d = venue ? Math.hypot(venue.streetX - atlas.localX, venue.streetZ - atlas.localZ) : Math.hypot(post.venueX - atlas.localX, post.venueZ - atlas.localZ);
    const onDuty = venue ? insideVenue(venue, atlas.localX, atlas.localZ) : d <= dutyRadius(post.postKey);
    if (onDuty && now - this.pingAt > DUTY_PING_S * 1000) this.ping(false);
    // The customer: the oldest waiting request; mark them, and serve within reach.
    const req = this.myRequests().sort((a, b) => Number(a.requestId - b.requestId))[0] ?? null;
    let serveLabel: string | null = null;
    let arrived = false;
    if (req) {
      const p = this.versonaPos(req.versonaId);
      if (p) {
        // In the bound room the arrived customer is PRESENTED at the counter (AtlasVenueModule publishes where).
        const cnt = venue ? venueCounter() : null;
        const here = cnt && cnt.binding === venue ? cnt : null;
        const shown = here?.spots.get(req.versonaId.toString());
        this.markAt(shown ? shown.x : p.x, shown ? shown.z : p.z, shown ? shown.y : undefined);
        const dc = Math.hypot(p.x - atlas.localX, p.z - atlas.localZ);
        arrived = !!venue && Math.hypot(p.x - venue.streetX, p.z - venue.streetZ) <= VENUE_ARRIVE_M;
        // Bound: once they have reached the door, from the staff side of the counter (anywhere inside a room that has
        // none; from the doorstep when this client is not in the room). Unbound: within reach of them, as before.
        const reach = venue
          ? mayServeAt(venue, SERVE_REACH_M, atlas.localX, atlas.localZ, p.x, p.z) && (!here || here.anywhere || here.dStaff <= COUNTER_REACH_M || dc <= SERVE_REACH_M)
          : dc <= SERVE_REACH_M;
        if (reach && onDuty) {
          interactFocus().claim({ id: 'post:serve', dist: venue ? (here && Number.isFinite(here.dStaff) ? Math.min(here.dStaff, dc) : Math.min(dc, 1)) : dc, priority: 4, label: interactPrompt('serve') });
          if (interactFocus().owns('post:serve')) { serveLabel = interactPrompt(`serve: ${req.need}`); this.serveId = req.requestId; }
        }
      } else this.dropMark();
    } else this.dropMark();
    if (!serveLabel) this.serveId = null;
    this.showServe(serveLabel);
    // The HUD strip.
    const lines = [
      venue
        ? `${postLabel(post.postKey)} at ${townName(post.settlementId)}${onDuty ? ` — on duty ${venuePlaceText(venue)}` : ` — ${Math.round(d)} m ${compassOf(venue.streetX - atlas.localX, venue.streetZ - atlas.localZ)} to ${venueTitle(venue)}: your post is kept inside`}`
        : `${postLabel(post.postKey)} at ${townName(post.settlementId)}${onDuty ? ' — on duty' : ` — ${Math.round(d)} m ${compassOf(post.venueX - atlas.localX, post.venueZ - atlas.localZ)} to your post`}`,
      `Today ${Math.floor(post.dutyTodayS / 60)} m · served ${post.served} · earned ${post.earned} ⓒ`,
    ];
    if (req) {
      const p = this.versonaPos(req.versonaId);
      if (venue && p) lines.push(arrived ? `Customer: ${req.need} — at the door: serve from behind the counter` : `Customer: ${req.need} — on the way (${Math.round(Math.hypot(p.x - venue.streetX, p.z - venue.streetZ))} m from the door)`);
      else lines.push(p ? `Customer: ${req.need} — ${Math.round(Math.hypot(p.x - atlas.localX, p.z - atlas.localZ))} m` : `Customer: ${req.need}`);
    }
    if (this.pingNote) lines.push(this.pingNote);
    const h = this.hud!;
    const sig = lines.join('\n');
    if (h.dataset.sig !== sig) { h.dataset.sig = sig; h.replaceChildren(...lines.map((l, i) => el('div', i === 0 ? '' : 'font-weight:500;color:var(--ax-soft)', l))); }
    if (h.style.display !== 'flex') h.style.display = 'flex';
  }

  private hudOff(): void { if (this.hud && this.hud.style.display !== 'none') this.hud.style.display = 'none'; }

  private ping(manual: boolean): void {
    this.pingAt = performance.now();
    const r = atlas.conn?.reducers as unknown as { postDutyPing?(a: Record<string, never>): Promise<void> } | undefined;
    r?.postDutyPing?.({}).then(() => { this.pingNote = ''; }).catch((e: Error) => { this.pingNote = String(e?.message ?? e).replace(/^SenderError:\s*/, ''); if (manual) bus.emit('atlas:toast', { text: this.pingNote, kind: 'error' }); });
  }

  private markAt(x: number, z: number, floorY?: number): void {
    if (!this.customerMark) {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const g = c.getContext('2d')!;
      g.fillStyle = '#ffd36b';
      g.beginPath(); g.arc(32, 32, 26, 0, Math.PI * 2); g.fill();
      g.fillStyle = '#231a0e';
      g.font = '900 44px Sora, sans-serif'; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillText('!', 32, 34);
      const t = new THREE.CanvasTexture(c);
      t.colorSpace = THREE.SRGBColorSpace;
      const m = new THREE.SpriteMaterial({ map: t, transparent: true, depthWrite: false, depthTest: false });
      markMrtOverlay(m);
      this.customerMark = new THREE.Sprite(m);
      this.customerMark.scale.set(0.55, 0.55, 1);
      this.customerMark.renderOrder = 5;
      this.customerMark.name = 'post-customer-mark';
    }
    if (!this.customerMark.parent) this.scene.add(this.customerMark);
    const y = floorY ?? (atlas.conn ? groundYIn(atlas.conn, atlas.currentWorldId, x, z) : 0); // indoors: the slab the customer is presented on
    this.customerMark.position.set(x, y + 2.4 + Math.sin(performance.now() / 300) * 0.08, z);
  }
  private dropMark(): void { if (this.customerMark?.parent) this.scene.remove(this.customerMark); }

  private showServe(text: string | null): void {
    const p = this.prompt;
    if (!p) return;
    const show = text !== null;
    if ((p.style.display !== 'none') !== show) p.style.display = show ? 'block' : 'none';
    if (show && p.textContent !== text) p.textContent = text;
  }

  private key(): void {
    if (this.serveId === null || !interactFocus().owns('post:serve')) return;
    const id = this.serveId;
    this.serveId = null;
    void act((atlas.conn?.reducers as unknown as { servePostRequest?(a: { requestId: bigint }): Promise<void> } | undefined)?.servePostRequest?.({ requestId: id }), 'Served — they go on their way content');
  }

  // ── The window ──────────────────────────────────────────────────────────────
  private open(tab?: string): void {
    if (tab) this.tab = tab;
    if (!this.win) {
      this.win = makeWindow({ id: 'town-life', title: 'Town', icon: 'trophy', width: 600, height: 620, minWidth: 460, minHeight: 380 });
      this.win.body.style.cssText += 'display:flex;flex-direction:column;gap:10px;padding:10px 12px;overflow:auto;font:13px var(--ax-font);color:var(--ax-text)';
    }
    this.win.setOpen(true);
    this.render();
  }

  private render(): void {
    const w = this.win;
    if (!w) return;
    const list = townTabs();
    if (!list.some((t) => t.key === this.tab) && list.length) this.tab = list[0].key;
    // SOFT RENDER (2026-09-25): the strip is patched in place and the tab's host survives a re-render of the SAME tab
    // (its content soft-patched by the tab's own renderer) — the window no longer rebuilds under a camp name or a relief
    // quantity being typed. A NEW host is made only when the tab changes: a tab module's `tabEl.isConnected` guard is
    // what stops its row callbacks drawing into another tab's host.
    const strip = tabs(list.map((t) => ({ key: t.key, label: t.label })), this.tab, (k) => { if (k !== this.tab) w.body.scrollTop = 0; this.tab = k; this.render(); });
    // EIGHT TABS WRAP (slice H, 2026-10-02 — the first picture of "My rooms"): at the window's 600 px the eighth tab ran
    // past the right border, cut to "My roo…" and only half clickable. The strip wraps to a second row instead.
    strip.style.flexWrap = 'wrap';
    const first = w.body.firstElementChild;
    if (!first || first === this.tabHost) w.body.prepend(strip);
    else if (!first.isEqualNode(strip)) first.replaceWith(strip);
    if (!this.tabHost || this.tabHostFor !== this.tab || this.tabHost.parentElement !== w.body) {
      const host = el('div', 'display:flex;flex-direction:column;gap:8px');
      if (this.tabHost?.parentElement === w.body) this.tabHost.replaceWith(host); else w.body.appendChild(host);
      this.tabHost = host;
      this.tabHostFor = this.tab;
    }
    this.renderTab();
  }
  private renderTab(): void {
    const host = this.tabHost;
    if (!host) return;
    const t = townTabs().find((x) => x.key === this.tab);
    t?.render(host);
  }

  // SOFT RENDER (2026-09-25): each tab builds into a scratch and is patched over the live host (lib/soft_render.ts).
  private renderPosts(host: HTMLElement): void { softRender(host, (h) => this.buildPosts(h)); }
  private buildPosts(host: HTMLElement): void {
    const r = atlas.conn?.reducers as unknown as Record<string, (a: unknown) => Promise<void>> | undefined;
    const post = this.myPost();
    host.appendChild(head('Service posts'));
    host.appendChild(muted('Hold one of a town\'s posts beside its own residents: stand your post at its venue, and the town sends you customers — real residents with a need — to serve in person. You draw a small wage for time on duty and a fee for every customer; a post you leave unworked for three days lapses.'));
    if (post) {
      const c = card();
      // INTERIOR VENUES: a bound post is kept in its building — say where, and measure to its door.
      const venue = venueOfPostRow(this.seedNow(), post);
      const px = venue ? venue.streetX : post.venueX, pz = venue ? venue.streetZ : post.venueZ;
      const d = Math.hypot(px - atlas.localX, pz - atlas.localZ);
      const onDuty = venue ? insideVenue(venue, atlas.localX, atlas.localZ) : d <= dutyRadius(post.postKey);
      c.appendChild(el('div', 'font-weight:600;color:var(--ax-hi)', `${postLabel(post.postKey)} at ${townName(post.settlementId)}`));
      const chips = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
      if (venue) chips.appendChild(chip(venuePlaceText(venue)));
      chips.appendChild(chip(onDuty ? 'on duty' : `${Math.round(d)} m away`, onDuty ? 'good' : 'warn'));
      chips.appendChild(chip(`today ${Math.floor(post.dutyTodayS / 60)} m`));
      chips.appendChild(chip(`all time ${Math.floor(post.totalDutyS / 3600)} h`));
      chips.appendChild(chip(`served ${post.served}`));
      chips.appendChild(chip(`earned ${post.earned} ⓒ`, 'warn'));
      c.appendChild(chips);
      const reqs = this.myRequests();
      c.appendChild(muted(reqs.length ? `Waiting on you: ${reqs.map((q) => q.need).join(' · ')}` : 'No customer is waiting — one comes every couple of minutes while you stand your post.'));
      const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
      row.appendChild(btn('Waypoint to your post', () => void act(atlas.conn?.reducers.createWaypoint({ name: `Post: ${postLabel(post.postKey)}`.slice(0, 32), posX: px, posZ: pz }), venue ? `Waypoint set on the door of ${venueTitle(venue)}` : 'Waypoint set on your post')));
      row.appendChild(btn('Leave the post', () => void act(r?.leaveServicePost?.({}), 'You left the post — your owed wage is paid', () => this.renderTab())));
      c.appendChild(row);
      host.appendChild(c);
      return;
    }
    const here = this.townHere();
    if (!here) { host.appendChild(muted('Stand in a town of the Commons to see the posts it keeps.')); return; }
    const tier = postTierOf(here.site.tier);
    host.appendChild(el('div', 'color:var(--ax-soft)', `${townName(here.sid)} (${here.site.tier}) keeps:`));
    for (const def of SERVICE_POSTS) {
      const n = def.count[tier];
      if (n <= 0) continue;
      const slot = this.venueOf(here.site, def.key);
      // INTERIOR VENUES: the slot's bound building, when it has one — the post is then taken inside it (its door is the way in).
      const venue = venueOfSlot(this.seedNow(), def.key, slot.x, slot.z);
      const v = venue ? { x: venue.streetX, z: venue.streetZ } : slot;
      const d = Math.hypot(v.x - atlas.localX, v.z - atlas.localZ);
      const canTake = venue ? insideVenue(venue, atlas.localX, atlas.localZ) : d <= dutyRadius(def.key);
      const holders = this.holdersOf(here.sid, def.key);
      const c = card();
      c.setAttribute('data-row', `post:${def.key}`);
      const top = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap');
      top.appendChild(el('span', 'font-weight:600;color:var(--ax-hi)', postLabel(def.key)));
      top.appendChild(chip(venue ? venuePlaceText(venue) : `at the ${isRoutePost(def.key) ? 'plaza (a walked beat)' : def.venue}`));
      top.appendChild(chip(`${Math.round(d)} m ${compassOf(v.x - atlas.localX, v.z - atlas.localZ)}`));
      if (holders.length) top.appendChild(chip(`held by ${holders.join(', ')}`, 'warn'));
      c.appendChild(top);
      c.appendChild(muted(`Serves ${def.demand}.`));
      const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
      if (holders.length === 0) {
        if (canTake) row.appendChild(btn('Take the post', () => void act(r?.takeServicePost?.({ postKey: def.key }), `You hold the ${postLabel(def.key)}'s post — stand it and customers will come`, () => this.renderTab()), true));
        else row.appendChild(btn(venue ? `Waypoint to ${venueTitle(venue)}` : `Waypoint to the ${isRoutePost(def.key) ? 'plaza' : def.venue}`, () => void act(atlas.conn?.reducers.createWaypoint({ name: `${postLabel(def.key)} post`.slice(0, 32), posX: v.x, posZ: v.z }), venue ? 'Waypoint set on its door — take the post inside, at the counter' : 'Waypoint set — take the post when you arrive')));
      }
      if (row.childElementCount) c.appendChild(row);
      host.appendChild(c);
    }
  }

  private renderFire(real: HTMLElement): void { softRender(real, (h) => this.buildFire(h, real)); }
  /** `host` is the scratch being built; `real` the live tab host a click re-renders. */
  private buildFire(host: HTMLElement, real: HTMLElement): void {
    const r = atlas.conn?.reducers as unknown as Record<string, (a: unknown) => Promise<void>> | undefined;
    host.appendChild(head('Fireworks'));
    host.appendChild(muted(`Launched a few metres in front of you. A rocket costs ${FIREWORK_SOV} ⓒ; a choreographed show — forty seconds of the sky's best — costs ${FIREWORK_SHOW_SOV} ⓒ. Everyone in sight sees them. The towns put on their own shows at night and at their festivals.`));
    const kind = el('div');
    kind.appendChild(tabs(FIREWORK_KINDS.map((k) => ({ key: k, label: FIREWORK_LABELS[k] })), this.fwKind, (k) => { this.fwKind = k as FireworkKind; this.renderFire(real); }));
    host.appendChild(field('Shell', kind));
    const pal = select(FIREWORK_PALETTES.map((p, i) => ({ value: String(i), label: p.name })), String(this.fwPalette));
    pal.addEventListener('change', () => { this.fwPalette = Number(pal.value); });
    host.appendChild(field('Colours', pal));
    const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    row.appendChild(btn(`Launch a ${FIREWORK_LABELS[this.fwKind]} (${FIREWORK_SOV} ⓒ)`, () => void act(r?.launchFirework?.({ kind: this.fwKind, palette: this.fwPalette }), ''), true));
    row.appendChild(btn(`Put on a show (${FIREWORK_SHOW_SOV} ⓒ)`, () => void act(r?.launchFireworkShow?.({}), 'The show begins')));
    host.appendChild(row);
    host.appendChild(head('Camp'));
    const me = atlas.conn?.identity;
    const camps = (atlas.conn?.db as unknown as { playerCamp?: { iter(): Iterable<CampRow> } } | undefined)?.playerCamp;
    const mine = me && camps ? [...camps.iter()].find((c) => c.owner.isEqual(me)) : undefined;
    host.appendChild(muted(`Pitch a camp where you stand: a fire ring with log seats that burns for ${CAMP_LIFE_S / 60} minutes. Anyone may sit by it — warming by a fire lifts your Social need (once every ten minutes). Towns keep their own fire circles; the residents gather there at night.`));
    if (mine) {
      const c = card();
      c.appendChild(el('div', 'font-weight:600', `${mine.name} — burns ${untilText(ms(mine.expiresAt) - Date.now())} more`));
      c.appendChild(muted(`${Math.round(Math.hypot(mine.x - atlas.localX, mine.z - atlas.localZ))} m ${compassOf(mine.x - atlas.localX, mine.z - atlas.localZ)} of you.`));
      c.appendChild(btn('Put the fire out', () => void act(r?.strikeCamp?.({}), 'Your fire is out', () => this.renderFire(real))));
      host.appendChild(c);
    } else {
      const name = input('the camp\'s name (optional)', '', 'text', '240px');
      name.maxLength = 32;
      name.setAttribute('data-k', 'camp-name');
      host.appendChild(field('Name', name));
      // The field ON SCREEN: a kept field holds the name being typed (2026-09-25).
      host.appendChild(btn('Pitch a camp here', () => void act(r?.pitchCamp?.({ name: (fieldByKey(real, 'camp-name') ?? name).value.trim() }), 'Camp pitched — E by the fire to sit and warm yourself', () => this.renderFire(real)), true));
    }
  }

  private renderBounties(real: HTMLElement): void { softRender(real, (h) => this.buildBounties(h)); }
  private buildBounties(host: HTMLElement): void {
    host.appendChild(head('Bounties'));
    host.appendChild(muted('Every town\'s bounty guild keeps a terminal near its guard post: post a price on a player\'s head, hire one of the guild\'s own hunters, or take a posted bounty and collect it the moment your mark goes down. Walk up to a terminal and press ' + interactKey() + '.'));
    const near = this.nearestStation('bounty_terminal');
    if (near) host.appendChild(this.nearCard(near, 'Nearest bounty terminal', 'Bounty terminal'));
    host.appendChild(btn('Open my bounties (read-only)', () => bus.emit('atlas:open-bounty-board', { stationId: '0', settlementId: near ? String(near.settlementId) : '0' })));
  }
  private renderContracts(real: HTMLElement): void { softRender(real, (h) => this.buildContracts(h)); }
  private buildContracts(host: HTMLElement): void {
    host.appendChild(head('Contracts'));
    host.appendChild(muted('Every town keeps a contract board by its market: courier a parcel of real goods to another town, walk as an escort, or bring food a town is short of. Walk up to a board and press ' + interactKey() + '.'));
    const near = this.nearestStation('contract_board');
    if (near) host.appendChild(this.nearCard(near, 'Nearest contract board', 'Contract board'));
    host.appendChild(btn('Open my contracts', () => bus.emit('atlas:open-contract-board', { stationId: '0', settlementId: near ? String(near.settlementId) : '0' })));
  }
  private nearestStation(kind: string): { x: number; z: number; settlementId: bigint } | null {
    const t = (atlas.conn?.db as unknown as { activityStation?: { iter(): Iterable<{ worldId: bigint; kind: string; x: number; z: number; settlementId: bigint }> } } | undefined)?.activityStation;
    if (!t || atlas.currentWorldId === null) return null;
    let best: { x: number; z: number; settlementId: bigint } | null = null, bd = Infinity;
    for (const s of t.iter()) {
      if (s.kind !== kind || s.worldId !== atlas.currentWorldId) continue;
      const d = Math.hypot(s.x - atlas.localX, s.z - atlas.localZ);
      if (d < bd) { bd = d; best = s; }
    }
    return best;
  }
  private nearCard(s: { x: number; z: number; settlementId: bigint }, title: string, label: string): HTMLDivElement {
    const c = card();
    const d = Math.hypot(s.x - atlas.localX, s.z - atlas.localZ);
    c.appendChild(el('div', 'font-weight:600', `${title}: ${townName(s.settlementId)}`));
    c.appendChild(muted(`${d > 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`} ${compassOf(s.x - atlas.localX, s.z - atlas.localZ)}`));
    c.appendChild(btn('Waypoint', () => void act(atlas.conn?.reducers.createWaypoint({ name: `${label}: ${townName(s.settlementId)}`.slice(0, 32), posX: s.x, posZ: s.z }), 'Waypoint set')));
    return c;
  }

  private state(): Record<string, unknown> {
    const post = this.myPost();
    return {
      open: !!this.win?.isOpen(), tab: this.tab, tabs: townTabs().map((t) => t.key),
      post: post ? { key: post.postKey, sid: String(post.settlementId), served: post.served, dutyTodayS: post.dutyTodayS } : null,
      requests: this.myRequests().map((r) => ({ id: String(r.requestId), versona: String(r.versonaId), need: r.need })),
      serveReady: this.serveId !== null,
      pingNote: this.pingNote,
    };
  }

  dispose(): void {
    this.offInteract?.(); this.offInteract = null;
    this.prompt?.remove();
    this.hud?.remove();
    this.dropMark();
  }
}
